import React from 'react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Textarea } from '../../../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../../../ui/card';
import { Label } from '../../../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../../../lib/auth/AuthContext';
import { Id } from '../../../../convex/_generated/dataModel';
import { PaymentPlanContent } from '../../../../types/pageBuilder';
import { DEFAULT_PAYMENT_PLAN_CONTENT } from '../../../../constants/pageBuilderDefaults';
import { useToast } from '../../../ui/use-toast';

interface PaymentPlanSectionProps {
  projectId: string;
  selectedLanguage: string;
  paymentPlanComponent: any;
  paymentPlanContent: PaymentPlanContent;
  setPaymentPlanContent: React.Dispatch<React.SetStateAction<PaymentPlanContent>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  refreshComponents: () => void;
}

export const PaymentPlanSection: React.FC<PaymentPlanSectionProps> = ({
  projectId,
  selectedLanguage,
  paymentPlanComponent,
  paymentPlanContent,
  setPaymentPlanContent,
  isLoading,
  setIsLoading,
  refreshComponents
}) => {
  const { useAuthenticatedMutation } = useAuth();
  const { toast } = useToast();
  const saveComponent = useAuthenticatedMutation('pageComponents:upsert');

  const handleSavePaymentPlanComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID and language are required'
      });
      return;
    }

    console.log('Saving payment plan component with data:', paymentPlanContent);
    setIsLoading(true);
    
    try {
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'paymentPlan',
        order: 500, // Fifth component
        content: paymentPlanContent,
        isActive: true,
        componentId: paymentPlanComponent?._id
      });
      
      console.log('Payment plan component saved with ID:', componentId);
      toast({
        title: 'Success',
        description: 'Payment plan section saved successfully'
      });
      
      // Refresh the component list without changing the active tab
      refreshComponents();
    } catch (error) {
      console.error('Error saving payment plan component:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save payment plan'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Add a payment plan
  const addPaymentPlan = () => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: [
        ...prev.plans,
        {
          id: crypto.randomUUID(),
          name: '',
          description: '',
          downPayment: {
            percentage: 20,
            dueDate: 'On Booking'
          },
          milestones: [],
          highlights: []
        }
      ]
    }));
  };

  // Remove a payment plan
  const removePaymentPlan = (id: string) => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: prev.plans.filter(plan => plan.id !== id)
    }));
  };

  // Update a payment plan's basic info
  const updatePaymentPlan = (id: string, field: 'name' | 'description', value: string) => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: prev.plans.map(plan => {
        if (plan.id === id) {
          return {
            ...plan,
            [field]: value
          };
        }
        return plan;
      })
    }));
  };

  // Update down payment details
  const updateDownPayment = (planId: string, field: 'percentage' | 'dueDate', value: any) => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: prev.plans.map(plan => {
        if (plan.id === planId) {
          return {
            ...plan,
            downPayment: {
              ...plan.downPayment,
              [field]: field === 'percentage' ? parseFloat(value) : value
            }
          };
        }
        return plan;
      })
    }));
  };

  // Add a milestone to a payment plan
  const addMilestone = (planId: string) => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: prev.plans.map(plan => {
        if (plan.id === planId) {
          return {
            ...plan,
            milestones: [
              ...plan.milestones,
              {
                percentage: 10,
                milestone: '',
                dueDate: '',
                description: ''
              }
            ]
          };
        }
        return plan;
      })
    }));
  };

  // Remove a milestone
  const removeMilestone = (planId: string, index: number) => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: prev.plans.map(plan => {
        if (plan.id === planId) {
          return {
            ...plan,
            milestones: plan.milestones.filter((_, i) => i !== index)
          };
        }
        return plan;
      })
    }));
  };

  // Update a milestone
  const updateMilestone = (planId: string, index: number, field: keyof PaymentPlanContent['plans'][0]['milestones'][0], value: any) => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: prev.plans.map(plan => {
        if (plan.id === planId) {
          const newMilestones = [...plan.milestones];
          newMilestones[index] = {
            ...newMilestones[index],
            [field]: field === 'percentage' ? parseFloat(value) : value
          };
          return {
            ...plan,
            milestones: newMilestones
          };
        }
        return plan;
      })
    }));
  };

  // Add a highlight to a payment plan
  const addHighlightToPaymentPlan = (planId: string) => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: prev.plans.map(plan => {
        if (plan.id === planId) {
          return {
            ...plan,
            highlights: [
              ...plan.highlights,
              {
                icon: 'calendar',
                title: '',
                description: ''
              }
            ]
          };
        }
        return plan;
      })
    }));
  };

  // Remove a highlight
  const removeHighlightFromPaymentPlan = (planId: string, index: number) => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: prev.plans.map(plan => {
        if (plan.id === planId) {
          return {
            ...plan,
            highlights: plan.highlights.filter((_, i) => i !== index)
          };
        }
        return plan;
      })
    }));
  };

  // Update a highlight
  const updatePaymentPlanHighlight = (planId: string, index: number, field: keyof PaymentPlanContent['plans'][0]['highlights'][0], value: string) => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: prev.plans.map(plan => {
        if (plan.id === planId) {
          const newHighlights = [...plan.highlights];
          newHighlights[index] = {
            ...newHighlights[index],
            [field]: value
          };
          return {
            ...plan,
            highlights: newHighlights
          };
        }
        return plan;
      })
    }));
  };

  const resetToDefault = () => {
    setPaymentPlanContent(DEFAULT_PAYMENT_PLAN_CONTENT);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment Plan</CardTitle>
        <CardDescription>
          Define the payment plan for the project
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="payment-plan-title">Payment Plan Title</Label>
          <Input
            id="payment-plan-title"
            value={paymentPlanContent.title}
            onChange={(e) => setPaymentPlanContent(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Enter payment plan title"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="payment-plan-description">Payment Plan Description</Label>
          <Textarea
            id="payment-plan-description"
            value={paymentPlanContent.description}
            onChange={(e) => setPaymentPlanContent(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Enter payment plan description"
            rows={3}
          />
        </div>
        
        <div className="mt-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Payment Plans</h3>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={addPaymentPlan}
              type="button"
            >
              Add Payment Plan
            </Button>
          </div>
          
          {paymentPlanContent.plans.length > 0 ? (
            <Tabs defaultValue={paymentPlanContent.plans[0].id} className="w-full">
              <TabsList className="mb-4 flex flex-wrap">
                {paymentPlanContent.plans.map((plan) => (
                  <TabsTrigger key={plan.id} value={plan.id}>
                    {plan.name || `Plan ${plan.id.substring(0, 4)}`}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              {paymentPlanContent.plans.map((plan) => (
                <TabsContent key={plan.id} value={plan.id} className="space-y-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-md font-medium">Plan Details</h4>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removePaymentPlan(plan.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove Plan
                    </Button>
                  </div>
                  
                  <div className="space-y-4 p-4 border rounded-md">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor={`plan-name-${plan.id}`}>Plan Name</Label>
                        <Input
                          id={`plan-name-${plan.id}`}
                          value={plan.name}
                          onChange={(e) => updatePaymentPlan(plan.id, 'name', e.target.value)}
                          placeholder="Standard Plan"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor={`plan-description-${plan.id}`}>Plan Description</Label>
                        <Textarea
                          id={`plan-description-${plan.id}`}
                          value={plan.description}
                          onChange={(e) => updatePaymentPlan(plan.id, 'description', e.target.value)}
                          placeholder="Description of the payment plan"
                          rows={2}
                        />
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-md space-y-2">
                      <h5 className="font-medium">Down Payment</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor={`down-payment-percentage-${plan.id}`}>Percentage (%)</Label>
                          <Input
                            id={`down-payment-percentage-${plan.id}`}
                            type="number"
                            value={plan.downPayment.percentage}
                            onChange={(e) => updateDownPayment(plan.id, 'percentage', e.target.value)}
                            placeholder="20"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor={`down-payment-due-date-${plan.id}`}>Due Date</Label>
                          <Input
                            id={`down-payment-due-date-${plan.id}`}
                            value={plan.downPayment.dueDate}
                            onChange={(e) => updateDownPayment(plan.id, 'dueDate', e.target.value)}
                            placeholder="On Booking"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Milestones Section */}
                  <div className="mt-6">
                    <div className="flex justify-between items-center mb-4">
                      <h5 className="font-medium">Payment Milestones</h5>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => addMilestone(plan.id)}
                        type="button"
                      >
                        Add Milestone
                      </Button>
                    </div>
                    
                    {plan.milestones.length === 0 ? (
                      <div className="text-center p-4 border border-dashed rounded-md">
                        <p className="text-sm text-gray-500">No milestones added yet. Click the button above to add a milestone.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {plan.milestones.map((milestone, index) => (
                          <div key={`${plan.id}-milestone-${index}`} className="border rounded-md p-4">
                            <div className="flex justify-between items-center mb-3">
                              <h6 className="text-sm font-medium">Milestone {index + 1}</h6>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => removeMilestone(plan.id, index)}
                                className="h-8 text-red-500 hover:text-red-700"
                              >
                                Remove
                              </Button>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <Label htmlFor={`milestone-percentage-${plan.id}-${index}`}>Percentage (%)</Label>
                                <Input
                                  id={`milestone-percentage-${plan.id}-${index}`}
                                  type="number"
                                  value={milestone.percentage}
                                  onChange={(e) => updateMilestone(plan.id, index, 'percentage', e.target.value)}
                                  placeholder="10"
                                />
                              </div>
                              
                              <div>
                                <Label htmlFor={`milestone-name-${plan.id}-${index}`}>Milestone Name</Label>
                                <Input
                                  id={`milestone-name-${plan.id}-${index}`}
                                  value={milestone.milestone}
                                  onChange={(e) => updateMilestone(plan.id, index, 'milestone', e.target.value)}
                                  placeholder="Construction Start"
                                />
                              </div>
                              
                              <div>
                                <Label htmlFor={`milestone-due-date-${plan.id}-${index}`}>Due Date</Label>
                                <Input
                                  id={`milestone-due-date-${plan.id}-${index}`}
                                  value={milestone.dueDate}
                                  onChange={(e) => updateMilestone(plan.id, index, 'dueDate', e.target.value)}
                                  placeholder="6 months after booking"
                                />
                              </div>
                              
                              <div>
                                <Label htmlFor={`milestone-description-${plan.id}-${index}`}>Description</Label>
                                <Textarea
                                  id={`milestone-description-${plan.id}-${index}`}
                                  value={milestone.description}
                                  onChange={(e) => updateMilestone(plan.id, index, 'description', e.target.value)}
                                  placeholder="Description of this milestone"
                                  rows={2}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Highlights Section */}
                  <div className="mt-6">
                    <div className="flex justify-between items-center mb-4">
                      <h5 className="font-medium">Plan Highlights</h5>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => addHighlightToPaymentPlan(plan.id)}
                        type="button"
                      >
                        Add Highlight
                      </Button>
                    </div>
                    
                    {plan.highlights.length === 0 ? (
                      <div className="text-center p-4 border border-dashed rounded-md">
                        <p className="text-sm text-gray-500">No highlights added yet. Click the button above to add a highlight.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {plan.highlights.map((highlight, index) => (
                          <div key={`${plan.id}-highlight-${index}`} className="border rounded-md p-4">
                            <div className="flex justify-between items-center mb-3">
                              <h6 className="text-sm font-medium">Highlight {index + 1}</h6>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => removeHighlightFromPaymentPlan(plan.id, index)}
                                className="h-8 text-red-500 hover:text-red-700"
                              >
                                Remove
                              </Button>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div>
                                <Label htmlFor={`highlight-icon-${plan.id}-${index}`}>Icon</Label>
                                <Select
                                  value={highlight.icon}
                                  onValueChange={(value) => updatePaymentPlanHighlight(plan.id, index, 'icon', value)}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select an icon" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="calendar">📅 Calendar</SelectItem>
                                    <SelectItem value="payment">💳 Payment</SelectItem>
                                    <SelectItem value="document">📄 Document</SelectItem>
                                    <SelectItem value="money">💰 Money</SelectItem>
                                    <SelectItem value="bank">🏦 Bank</SelectItem>
                                    <SelectItem value="contract">📋 Contract</SelectItem>
                                    <SelectItem value="security">🔒 Security</SelectItem>
                                    <SelectItem value="handshake">🤝 Handshake</SelectItem>
                                    <SelectItem value="shield">🛡️ Shield</SelectItem>
                                    <SelectItem value="clock">⏰ Clock</SelectItem>
                                    <SelectItem value="percent">📊 Percent</SelectItem>
                                    <SelectItem value="calculator">🧮 Calculator</SelectItem>
                                    <SelectItem value="key">🔑 Key</SelectItem>
                                    <SelectItem value="home">🏠 Home</SelectItem>
                                    <SelectItem value="building">🏢 Building</SelectItem>
                                    <SelectItem value="star">⭐ Star</SelectItem>
                                    <SelectItem value="trophy">🏆 Trophy</SelectItem>
                                    <SelectItem value="diamond">💎 Diamond</SelectItem>
                                    <SelectItem value="rocket">🚀 Rocket</SelectItem>
                                    <SelectItem value="chart">📈 Chart</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              <div>
                                <Label htmlFor={`highlight-title-${plan.id}-${index}`}>Title</Label>
                                <Input
                                  id={`highlight-title-${plan.id}-${index}`}
                                  value={highlight.title}
                                  onChange={(e) => updatePaymentPlanHighlight(plan.id, index, 'title', e.target.value)}
                                  placeholder="Flexible Payment"
                                />
                              </div>
                              
                              <div>
                                <Label htmlFor={`highlight-description-${plan.id}-${index}`}>Description</Label>
                                <Input
                                  id={`highlight-description-${plan.id}-${index}`}
                                  value={highlight.description}
                                  onChange={(e) => updatePaymentPlanHighlight(plan.id, index, 'description', e.target.value)}
                                  placeholder="Easy payment schedule"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          ) : (
            <div className="text-center p-4 border border-dashed rounded-md">
              <p className="text-sm text-gray-500">No payment plans added yet. Click the button above to add one.</p>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          variant="outline"
          onClick={resetToDefault}
          disabled={isLoading}
        >
          Reset to Default
        </Button>
        <Button 
          onClick={handleSavePaymentPlanComponent}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : 'Save Payment Plan'}
        </Button>
      </CardFooter>
    </Card>
  );
}; 